import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ShieldCheck, Radar, Crosshair } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * EIGOO Master Partners Hub · /partners
 *
 * Sovereign Aesthetic refit per /app/design_guidelines.json:
 *   - Deep space black canvas (#050A0F) with neon/gold accent grid.
 *   - Montserrat headings (uppercase, tight tracking) · Inter body.
 *   - Sharp-edged tactical cards (rounded-none) with gold or neon hover glow.
 *   - "[ ACCESS SIGNAL ]" CTA opens the cloaked /go/:slug redirect.
 */
export default function Partners() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "EIGOO · Master Partners Hub";
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/partners`);
                if (alive) setPartners(data.partners || []);
            } catch (e) {
                console.error("partners fetch failed", e);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    return (
        <div
            data-testid="partners-hub"
            className="min-h-screen text-white"
            style={{
                background:
                    "radial-gradient(ellipse at top, #0D1824 0%, #050A0F 55%, #02060A 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            {/* Top sentinel ribbon */}
            <div
                className="border-b"
                style={{ borderColor: "#1E3654", background: "rgba(5,10,15,0.7)" }}
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="partners-home-link"
                        className="text-[11px] uppercase tracking-[0.3em]"
                        style={{
                            color: "#E8C56C",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                        }}
                    >
                        ◂ Return to Bridge
                    </Link>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em]"
                         style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}>
                        <span
                            className="inline-block w-2 h-2 rounded-full animate-pulse"
                            style={{ background: "#00E5FF", boxShadow: "0 0 10px rgba(0,229,255,0.6)" }}
                        />
                        Sovereign Network · Live
                    </div>
                </div>
            </div>

            {/* Hero */}
            <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
                <div
                    className="text-[11px] md:text-xs uppercase tracking-[0.4em] mb-6"
                    style={{
                        color: "#00E5FF",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                    data-testid="partners-overline"
                >
                    ◢ Master Partners Hub · Vetted Signals Only
                </div>
                <h1
                    data-testid="partners-h1"
                    className="text-5xl md:text-6xl lg:text-7xl uppercase font-black leading-[0.95] tracking-tighter"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                >
                    The Sovereign{" "}
                    <span style={{ color: "#E8C56C", textShadow: "0 0 30px rgba(232,197,108,0.35)" }}>
                        Affiliate Roster
                    </span>
                </h1>
                <p
                    className="mt-6 max-w-3xl text-base md:text-lg leading-relaxed"
                    style={{ color: "#829AB1" }}
                >
                    Curated arsenal of platforms the Fleet runs on. Every signal is
                    field-tested by the Director. Tap{" "}
                    <span style={{ color: "#E8C56C", fontWeight: 700 }}>[ ACCESS SIGNAL ]</span>{" "}
                    to deploy through our cloaked routing layer.
                </p>

                {/* Crest row */}
                <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.3em]"
                     style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}>
                    <span className="inline-flex items-center gap-2">
                        <ShieldCheck size={14} style={{ color: "#E8C56C" }} /> Field-Tested
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Radar size={14} style={{ color: "#00E5FF" }} /> Cloak-Routed
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Crosshair size={14} style={{ color: "#E8C56C" }} /> Director-Approved
                    </span>
                </div>
            </header>

            {/* Grid */}
            <main className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
                {loading ? (
                    <div
                        data-testid="partners-loading"
                        className="text-center py-24 text-sm uppercase tracking-[0.3em]"
                        style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}
                    >
                        Acquiring Signal Lock…
                    </div>
                ) : (
                    <div
                        data-testid="partners-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {partners.map((p, idx) => (
                            <PartnerCard key={p.slug} partner={p} index={idx} />
                        ))}
                    </div>
                )}

                {/* Disclosure ribbon */}
                <div
                    data-testid="partners-disclosure"
                    className="mt-16 border-t pt-6 text-[11px] leading-relaxed max-w-3xl"
                    style={{
                        borderColor: "#1E3654",
                        color: "#5C7894",
                        fontFamily: "'Inter', sans-serif",
                    }}
                >
                    <span
                        className="inline-block mr-2 uppercase tracking-[0.3em]"
                        style={{ color: "#E8C56C", fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                    >
                        Disclosure ·
                    </span>
                    EIGOO Inc. may receive affiliate consideration when an
                    Operative deploys through these signals. Routing is logged for
                    transparency on the Director Bridge HUD.
                </div>
            </main>
        </div>
    );
}

function PartnerCard({ partner, index }) {
    const isGold = (partner.accent || "gold") === "gold";
    const ring = isGold ? "#E8C56C" : "#00E5FF";
    const glow = isGold ? "rgba(232,197,108,0.45)" : "rgba(0,229,255,0.5)";

    return (
        <article
            data-testid={`partner-card-${partner.slug}`}
            className="group relative flex flex-col p-7 transition-all duration-300"
            style={{
                background: "linear-gradient(180deg, #0D1824 0%, #0A1320 100%)",
                border: "1px solid #1E3654",
                animation: `partnerFadeIn 0.6s ease-out ${index * 0.06}s both`,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = ring;
                e.currentTarget.style.boxShadow = `0 0 30px ${glow}`;
                e.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#1E3654";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            {/* Corner ticks · tactical HUD chrome */}
            <span aria-hidden className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: ring }} />

            <div className="flex items-start justify-between mb-5">
                <span
                    className="text-[10px] uppercase tracking-[0.3em] px-2 py-1"
                    style={{
                        color: ring,
                        background: `${isGold ? "rgba(232,197,108,0.08)" : "rgba(0,229,255,0.08)"}`,
                        border: `1px solid ${ring}33`,
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                    }}
                >
                    {partner.category}
                </span>
                <span
                    className="font-mono text-[10px]"
                    style={{ color: "#5C7894", letterSpacing: "0.2em" }}
                >
                    #{String(index + 1).padStart(2, "0")}
                </span>
            </div>

            <h3
                data-testid={`partner-name-${partner.slug}`}
                className="text-2xl md:text-3xl uppercase font-black leading-tight tracking-tight"
                style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
            >
                {partner.name}
            </h3>

            <p
                className="mt-4 text-base leading-relaxed flex-1"
                style={{ color: "#A9BCD2", fontFamily: "'Inter', sans-serif" }}
            >
                {partner.signal}
            </p>

            <a
                data-testid={`partner-cta-${partner.slug}`}
                href={partner.go_url}
                rel="noopener noreferrer sponsored"
                className="mt-8 inline-flex items-center justify-center gap-3 px-5 py-3 uppercase tracking-[0.25em] text-xs font-bold transition-all duration-300"
                style={{
                    fontFamily: "'Montserrat', sans-serif",
                    color: "#E8C56C",
                    border: "1px solid #E8C56C",
                    background: "rgba(232,197,108,0.04)",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#E8C56C";
                    e.currentTarget.style.color = "#050A0F";
                    e.currentTarget.style.boxShadow = "0 0 20px rgba(232,197,108,0.55)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(232,197,108,0.04)";
                    e.currentTarget.style.color = "#E8C56C";
                    e.currentTarget.style.boxShadow = "none";
                }}
            >
                [ ACCESS SIGNAL ] <ArrowUpRight size={14} />
            </a>
        </article>
    );
}

// Inject keyframes once (avoids touching global App.css)
if (typeof document !== "undefined" && !document.getElementById("partners-fade-kf")) {
    const style = document.createElement("style");
    style.id = "partners-fade-kf";
    style.textContent = `
@keyframes partnerFadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
}`;
    document.head.appendChild(style);
}
